/**
 * FacilityDirectory Component
 * Sortable and filterable directory of approved waste facilities
 */

import { useState, useMemo } from 'react';
import type { Facility } from '../../types/county';

interface FacilityDirectoryProps {
  facilities: Facility[];
  countyName: string;
}

type SortField = 'name' | 'city' | 'distance';
type SortDirection = 'asc' | 'desc';

export function FacilityDirectory({ facilities, countyName }: FacilityDirectoryProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [materialFilter, setMaterialFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Extract unique materials from all facilities
  const availableMaterials = useMemo(() => {
    const materials = new Set<string>();
    facilities.forEach(facility => {
      facility.materialsAccepted.forEach(material => materials.add(material));
    });
    return Array.from(materials).sort();
  }, [facilities]);

  // Filter and sort facilities
  const filteredFacilities = useMemo(() => {
    let result = [...facilities];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(facility =>
        facility.name.toLowerCase().includes(term) ||
        facility.city.toLowerCase().includes(term) ||
        facility.address.toLowerCase().includes(term)
      );
    }

    // Apply material filter
    if (materialFilter !== 'all') {
      result = result.filter(facility =>
        facility.materialsAccepted.includes(materialFilter)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'city':
          comparison = a.city.localeCompare(b.city);
          break;
        case 'distance':
          comparison = (a.distance || 999) - (b.distance || 999);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [facilities, searchTerm, materialFilter, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <section id="facilities" className="py-12 bg-neutral-50 border-b border-neutral-200">
      <div className="container mx-auto px-4 max-w-6xl">
        <h2 className="text-3xl font-bold text-neutral-900 mb-3">
          Approved Facility Directory
        </h2>
        <p className="text-neutral-600 mb-6">
          {facilities.length} approved waste management facilities in {countyName} County
        </p>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label htmlFor="facility-search" className="block text-sm font-medium text-neutral-700 mb-2">
                Search Facilities
              </label>
              <input
                id="facility-search"
                type="text"
                placeholder="Search by name, city, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                aria-label="Search facilities"
              />
            </div>

            {/* Material Filter */}
            <div>
              <label htmlFor="material-filter" className="block text-sm font-medium text-neutral-700 mb-2">
                Filter by Material
              </label>
              <select
                id="material-filter"
                value={materialFilter}
                onChange={(e) => setMaterialFilter(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                aria-label="Filter by material type"
              >
                <option value="all">All Materials</option>
                {availableMaterials.map(material => (
                  <option key={material} value={material}>
                    {material}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Sort Controls */}
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <span className="text-sm text-neutral-600 mr-2">Sort by:</span>
            <button
              onClick={() => handleSort('name')}
              className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                sortField === 'name'
                  ? 'bg-primary-100 border-primary-300 text-primary-700 font-medium'
                  : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50'
              }`}
              aria-label="Sort by facility name"
            >
              Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSort('city')}
              className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                sortField === 'city'
                  ? 'bg-primary-100 border-primary-300 text-primary-700 font-medium'
                  : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50'
              }`}
              aria-label="Sort by city"
            >
              City {sortField === 'city' && (sortDirection === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSort('distance')}
              className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                sortField === 'distance'
                  ? 'bg-primary-100 border-primary-300 text-primary-700 font-medium'
                  : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50'
              }`}
              aria-label="Sort by distance"
            >
              Distance {sortField === 'distance' && (sortDirection === 'asc' ? '↑' : '↓')}
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm text-neutral-600">
          Showing {filteredFacilities.length} of {facilities.length} facilities
        </div>

        {/* Facility List - Desktop Table View */}
        <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-700 uppercase tracking-wider">
                    Facility Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-700 uppercase tracking-wider">
                    Location
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-700 uppercase tracking-wider">
                    Materials Accepted
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-700 uppercase tracking-wider">
                    Contact
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {filteredFacilities.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-neutral-500">
                      No facilities found matching your criteria
                    </td>
                  </tr>
                ) : (
                  filteredFacilities.map((facility) => (
                    <tr key={facility.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-neutral-900">{facility.name}</div>
                        {facility.countyApproved && (
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                            County Approved
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-700">
                        <div>{facility.city}</div>
                        <div className="text-xs text-neutral-500">{facility.address}</div>
                        {facility.distance && (
                          <div className="text-xs text-primary-600 mt-1">
                            {facility.distance.toFixed(1)} miles
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {facility.materialsAccepted.slice(0, 3).map((material, idx) => (
                            <span
                              key={idx}
                              className="inline-block px-2 py-1 text-xs bg-neutral-100 text-neutral-700 rounded"
                            >
                              {material}
                            </span>
                          ))}
                          {facility.materialsAccepted.length > 3 && (
                            <span className="inline-block px-2 py-1 text-xs text-neutral-500">
                              +{facility.materialsAccepted.length - 3} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {facility.phone && (
                          <a
                            href={`tel:${facility.phone}`}
                            className="text-primary-600 hover:text-primary-700 block"
                          >
                            {facility.phone}
                          </a>
                        )}
                        {facility.website && (
                          <a
                            href={facility.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-700 text-xs block mt-1"
                          >
                            Visit Website →
                          </a>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Facility List - Mobile Card View */}
        <div className="lg:hidden space-y-4">
          {filteredFacilities.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center text-neutral-500">
              No facilities found matching your criteria
            </div>
          ) : (
            filteredFacilities.map((facility) => (
              <div
                key={facility.id}
                className="bg-white rounded-lg shadow-sm border border-neutral-200 p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-neutral-900">{facility.name}</h3>
                  {facility.countyApproved && (
                    <span className="inline-block px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                      Approved
                    </span>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="text-neutral-700">
                    <div className="font-medium">{facility.city}</div>
                    <div className="text-xs text-neutral-500">{facility.address}</div>
                  </div>

                  {facility.distance && (
                    <div className="text-primary-600 text-xs">
                      {facility.distance.toFixed(1)} miles away
                    </div>
                  )}

                  <div>
                    <div className="text-xs text-neutral-500 mb-1">Materials Accepted:</div>
                    <div className="flex flex-wrap gap-1">
                      {facility.materialsAccepted.map((material, idx) => (
                        <span
                          key={idx}
                          className="inline-block px-2 py-0.5 text-xs bg-neutral-100 text-neutral-700 rounded"
                        >
                          {material}
                        </span>
                      ))}
                    </div>
                  </div>

                  {(facility.phone || facility.website) && (
                    <div className="pt-2 border-t border-neutral-200 flex gap-3">
                      {facility.phone && (
                        <a
                          href={`tel:${facility.phone}`}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {facility.phone}
                        </a>
                      )}
                      {facility.website && (
                        <a
                          href={facility.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700"
                        >
                          Website →
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
